import { TableIds, useTableStore } from "@/store/tables";
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

interface TableInstanceHookProps<T extends object, K extends object> {
  data: T[];
  columns: ColumnDef<T | K>[];
  tableId: TableIds;
  subRowsKey?: keyof T;
  defaultColumnSize?: { min: number; max: number };
}

function hasSubRows<T extends object, K extends object>(
  row: T | K,
  subRowsKey: keyof T
): row is T & Record<keyof T, K[]> {
  return subRowsKey in row && Array.isArray((row as T)[subRowsKey]);
}
export const TableInstanceHook = <T extends object, K extends object>({
  data = [],
  columns,
  tableId,
  subRowsKey = "products" as keyof T,
  defaultColumnSize = { min: 150, max: 600 },
}: TableInstanceHookProps<T, K>) => {
  const { getTableSettings, setTableSettings } = useTableStore((state) => ({
    getTableSettings: state.getTableSettings,
    setTableSettings: state.setTableSettings,
  }));
  const savedSettings = getTableSettings(tableId);
  const columnVisibility = savedSettings.columnVisibility;
  const columnOrder = useMemo(() => {
    return savedSettings.columnOrder.length > 0
      ? savedSettings.columnOrder
      : columns.map((c) => c.id!);
  }, [savedSettings.columnOrder, columns]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable<T | K>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: defaultColumnSize.min,
      maxSize: defaultColumnSize.max,
    },

    getSubRows: (row) => {
      if (hasSubRows(row, subRowsKey)) {
        return (row as T)[subRowsKey] as K[];
      }
      return undefined;
    },
    getRowCanExpand: (row) => {
      const subRows = hasSubRows(row.original, subRowsKey)
        ? ((row.original as T)[subRowsKey] as K[])
        : undefined;

      return subRows ? subRows.length > 0 : false;
    },
    // getRowCanExpand: (row) => row.subRows?.length > 0,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: (updater) => {
      setTableSettings(tableId, {
        ...savedSettings,
        columnVisibility:
          updater instanceof Function ? updater(savedSettings.columnVisibility) : updater,
      });
    },
    filterFromLeafRows: true,
    onColumnOrderChange: (updater) => {
      setTableSettings(tableId, {
        ...savedSettings,
        columnOrder: updater instanceof Function ? updater(savedSettings.columnOrder) : updater,
      });
    },
    onSortingChange: setSorting,

    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      sorting,
    },
  });
  const info = table.getState().columnSizingInfo;
  const size = table.getState().columnSizing;
  const headers = table.getFlatHeaders();
  const columnSizeVars = useMemo(() => {
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, size, columnVisibility, columnOrder, headers]);
  return {
    columnSizeVars,
    table,
    columnOrder,
  };
};
